import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Toaster, toast } from 'sonner';
import { 
  Sun, 
  Moon, 
  LogOut, 
  Bell,
  ArrowLeft,
  TrendingDown,
  TrendingUp,
  BarChart3,
  AlertTriangle,
  Trash2,
  Loader2
} from 'lucide-react';
import { formatCurrency, formatRank } from '../lib/utils';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function Alerts() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/alerts`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setAlerts(data.alerts);
      }
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const toggleAlert = async (alertId) => {
    try {
      const response = await fetch(`${API_URL}/api/alerts/${alertId}`, {
        method: 'PUT',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setAlerts((prev) =>
          prev.map((a) =>
            a.alert_id === alertId ? { ...a, is_active: data.is_active } : a
          )
        );
        toast.success(data.is_active ? 'Alert activated' : 'Alert paused');
      }
    } catch (error) {
      toast.error('Failed to update alert');
    }
  };

  const deleteAlert = async (alertId) => {
    try {
      const response = await fetch(`${API_URL}/api/alerts/${alertId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        setAlerts((prev) => prev.filter((a) => a.alert_id !== alertId));
        toast.success('Alert deleted');
      }
    } catch (error) {
      toast.error('Failed to delete alert');
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'PRICE_DROP': return <TrendingDown className="h-5 w-5 text-emerald-500" />;
      case 'PROFIT_THRESHOLD': return <TrendingUp className="h-5 w-5 text-gold" />;
      case 'RANK_IMPROVEMENT': return <BarChart3 className="h-5 w-5 text-blue-500" />;
      case 'PROFIT_DROPPED': return <TrendingDown className="h-5 w-5 text-rose-500" />;
      case 'HIGH_IMPORT_RISK': return <AlertTriangle className="h-5 w-5 text-orange-500" />;
      default: return <Bell className="h-5 w-5" />;
    }
  };

  const getAlertLabel = (type) => {
    switch (type) {
      case 'PRICE_DROP': return 'Price Drop Alert';
      case 'PROFIT_THRESHOLD': return 'Profit Threshold Met';
      case 'RANK_IMPROVEMENT': return 'Rank Improvement';
      case 'PROFIT_DROPPED': return 'Profit Dropped';
      case 'HIGH_IMPORT_RISK': return 'High Import Risk';
      default: return type;
    }
  };

  const getThresholdDisplay = (alert) => {
    switch (alert.alert_type) {
      case 'PRICE_DROP':
      case 'PROFIT_THRESHOLD':
      case 'PROFIT_DROPPED':
        return `Target: ${formatCurrency(alert.threshold_value)}`;
      case 'RANK_IMPROVEMENT':
        return `Target: ${formatRank(alert.threshold_value)}`;
      default:
        return '';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Bell className="h-7 w-7 text-gold" />
              <span className="font-heading text-lg font-bold">Alert Manager</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={logout}>
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">Total Alerts</div>
              <div className="text-2xl font-bold font-mono">{alerts.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">Active</div>
              <div className="text-2xl font-bold font-mono text-emerald-500">
                {alerts.filter(a => a.is_active).length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">Paused</div>
              <div className="text-2xl font-bold font-mono text-muted-foreground">
                {alerts.filter(a => !a.is_active).length}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-heading text-xl font-bold">Your Alerts</h2>
          <Button variant="outline" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </Button>
        </div>

        {/* Alerts List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-gold" />
          </div>
        ) : alerts.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Bell className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-heading text-lg font-bold mb-2">No Alerts Yet</h3>
              <p className="text-muted-foreground mb-4">
                Create alerts from the dashboard to get notified about price changes and profit opportunities.
              </p>
              <Button onClick={() => navigate('/dashboard')} className="btn-gold">
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {alerts.map((alert) => (
              <Card 
                key={alert.alert_id} 
                className={`transition-all ${!alert.is_active ? 'opacity-60' : ''}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="mt-1">
                        {getAlertIcon(alert.alert_type)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium">{getAlertLabel(alert.alert_type)}</h3>
                          <Badge variant={alert.is_active ? "default" : "secondary"}>
                            {alert.is_active ? 'Active' : 'Paused'}
                          </Badge>
                        </div>
                        {alert.book && (
                          <div className="text-sm text-muted-foreground mb-1">
                            {alert.book.title} • ISBN: {alert.book.isbn}
                          </div>
                        )}
                        {alert.threshold_value && (
                          <div className="text-sm font-mono text-gold">
                            {getThresholdDisplay(alert)}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Switch
                        checked={alert.is_active}
                        onCheckedChange={() => toggleAlert(alert.alert_id)}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        onClick={() => deleteAlert(alert.alert_id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
